import React, { useEffect, useRef, useState } from 'react';

const CHART_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.min.js';

const DATASETS_CONFIG = [
  { label: 'Maker',      key: 'makerCharge',      color: '#378ADD' },
  { label: 'Loading',    key: 'loadingCharge',     color: '#1D9E75' },
  { label: 'Admin',      key: 'adminCharge',       color: '#7F77DD' },
  { label: 'UPI',        key: 'upiCharge',         color: '#EF9F27' },
  { label: 'Additional', key: 'additionalCharge',  color: '#D85A30' },
];

const TrendChart = ({ data, loading }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const [chartReady, setChartReady] = useState(!!window.Chart);

  useEffect(() => {
    if (window.Chart) { setChartReady(true); return; }
    if (document.querySelector(`script[src="${CHART_CDN}"]`)) return;
    const script = document.createElement('script');
    script.src = CHART_CDN;
    script.onload = () => setChartReady(true);
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (!chartReady || !canvasRef.current || !data || data.length === 0 || loading) return;

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const tickColor = isDark ? '#9CA3AF' : '#6B7280';
    const gridColor = isDark ? 'rgba(55,65,81,0.5)' : 'rgba(229,231,235,0.8)';

    chartRef.current = new window.Chart(canvasRef.current, {
      type: 'bar',
      data: {
        labels: data.map(d => d.label),
        datasets: DATASETS_CONFIG.map(cfg => ({
          label: cfg.label,
          data: data.map(d => parseFloat((Number(d[cfg.key] || 0) / 1000).toFixed(2))),
          backgroundColor: cfg.color,
          stack: 'total',
          borderRadius: 2,
        })),
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: { color: tickColor, font: { size: 11 }, padding: 12 },
          },
          tooltip: {
            callbacks: {
              label: (ctx) => ` ${ctx.dataset.label}: ₹${ctx.raw}K`,
            },
          },
        },
        scales: {
          x: {
            stacked: true,
            ticks: { color: tickColor, font: { size: 11 } },
            grid: { color: gridColor },
          },
          y: {
            stacked: true,
            ticks: {
              color: tickColor,
              font: { size: 11 },
              callback: (v) => `₹${v}K`,
            },
            grid: { color: gridColor },
          },
        },
      },
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [chartReady, data, loading]);

  if (loading) {
    return <div className="h-64 bg-gray-100 rounded-lg animate-pulse" />;
  }

  if (!data || data.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-400 text-sm">
        No trend data available
      </div>
    );
  }

  return (
    <div className="relative" style={{ height: '256px' }}>
      <canvas ref={canvasRef} />
    </div>
  );
};

export default TrendChart;
